import { useEffect, useMemo, useState } from "react";
import axios from "../../utils/axiosInstance";
import DailyReportTable from "./DailyReportTable";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import DailyReportDowntimeTable from "./DailyReportDowntimeTable";
const getLast8Days = () => {
  const days = [];
  const today = new Date();

  for (let i = 1; i <= 8; i++) {
    const past = new Date(today);
    past.setDate(today.getDate() - i);

    const year = past.getFullYear();
    const month = past.getMonth() + 1;
    const day = past.getDate();

    days.push({
      year,
      month,
      day,
      label: past.toLocaleDateString("en-US", {
        weekday: "short",
        day: "numeric",
        month: "short",
        year: "numeric",
      }),
      value: `${year}-${month}-${day}`,
    });
  }

  return days;
};

const DailyDownPerGroup = ({ group }) => {
  const [devices, setDevices] = useState([]);
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");

  const dayOptions = useMemo(() => getLast8Days(), []);
  const [selectedDay, setSelectedDay] = useState(dayOptions[0]?.value);

  // ================== Fetch devices ==================
  useEffect(() => {
    if (!group?.id) return;

    const fetchDevices = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_GATEAWAY}/devices?groupId=${group.id}`,
        );
        setDevices(res.data || []);
      } catch (err) {
        console.error("Failed to fetch devices", err);
      }
    };

    fetchDevices();
  }, [group?.id]);

  // ================== Fetch daily downtime report ==================
  const fetchDailyDowntimeReport = async (dayValue) => {
    const [year, month, day] = dayValue.split("-").map(Number);

    try {
      setLoading(true);
      setError(null);

      const payload = {
        unitList: devices.map((d) => d.id),
        year: year,
        month: month,
        day: day,
      };

      const res = await axios.post(
        `${process.env.REACT_APP_API_GATEAWAY}/reports/downtime-reports/`,
        payload,
        {
          headers: {
            id: "anything",
            "Content-Type": "application/json",
          },
        },
      );

      //setReportData(res.data.data.response);
      console.log(payload);
      setReportData(res.data.response);
    } catch (err) {
      console.error(err);
      setError("Failed to load daily report");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!devices.length || !selectedDay) return;
    fetchDailyDowntimeReport(selectedDay);
  }, [devices, selectedDay]);

  // ================== Helpers ==================
  const unitMap = useMemo(
    () =>
      devices.reduce((acc, u) => {
        acc[u.id] = u.name;
        return acc;
      }, {}),
    [devices],
  );

  // ================== Normalize backend data ==================
  const normalizedData = useMemo(() => {
    if (!reportData) return [];

    const rows = [];

    for (const [unitId, events] of Object.entries(reportData)) {
      if (!Array.isArray(events)) continue;

      for (const e of events) {
        rows.push({
          unitId,
          unitName: unitMap[unitId] || unitId,

          timeStart: new Date(e.timeStart * 1000).toLocaleString(),
          timeEnd: new Date(e.timeEnd * 1000).toLocaleString(),

          faultCode: e.faultCode,
          fault: e.fault,
          faultType: e.faultType,

          downtime: Number(e.downtime.toFixed(2)) || 0,
          customerTime: Number(e.customerTime.toFixed(2)) || 0,
          companyTime: Number(e.companyTime.toFixed(2)) || 0,

          ctrlType: e.ctrlType,
        });
      }
    }

    return rows;
  }, [reportData, unitMap]);

  // ================== Search filter ==================
  const filteredReportData = useMemo(() => {
    const q = searchQuery.toLowerCase();

    return normalizedData.filter((r) => {
      return (
        r.unitName.toLowerCase().includes(q) ||
        r.timeStart.toLowerCase().includes(q) ||
        r.timeEnd.toLowerCase().includes(q) ||
        r.faultCode.toString().includes(q) ||
        r.downtime.toString().includes(q) ||
        r.fault.toString().includes(q) ||
        r.downtime.toString().includes(q) ||
        r.customerTime.toString().includes(q) ||
        r.companyTime.toString().includes(q) ||
        r.ctrlType.toString().includes(q)
      );
    });
  }, [normalizedData, searchQuery]);

  // ================== Export ==================
  const exportToCSV = () => {
    if (!filteredReportData.length) return;

    const headers = [
      "Unit Name",
      "Time Start",
      "Time End",
      "Fault Code",
      "Fault",
      "Fault Type",
      "Downtime (hrs)",
      "Customer Time (hrs)",
      "Company Time (hrs)",
      "Control Type",
    ];

    const rows = filteredReportData.map((r) => [
      r.unitName,
      r.timeStart,
      r.timeEnd,
      r.faultCode,
      r.fault,
      r.faultType,
      r.downtime,
      r.customerTime,
      r.companyTime,
      r.ctrlType,
    ]);

    const csv = [headers, ...rows]
      .map((row) => row.map((f) => `"${String(f ?? "")}"`).join(","))
      .join("\n");

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "Daily_Downtime_Report.csv";
    link.click();
  };

  const exportToExcel = () => {
    if (!filteredReportData.length) return;

    const headers = [
      "Unit Name",
      "Time Start",
      "Time End",
      "Fault Code",
      "Fault",
      "Fault Type",
      "Downtime (hrs)",
      "Customer Time (hrs)",
      "Company Time (hrs)",
      "Control Type",
    ];

    const rows = filteredReportData.map((r) => [
      r.unitName,
      r.timeStart,
      r.timeEnd,
      r.faultCode,
      r.fault,
      r.faultType,
      r.downtime,
      r.customerTime,
      r.companyTime,
      r.ctrlType,
    ]);

    const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Daily Downtime Report");
    XLSX.writeFile(wb, "Daily_Downtime_Report.xlsx");
  };

  const exportToPDF = () => {
    if (!filteredReportData.length) return;

    const doc = new jsPDF("l", "pt", "a4");
    doc.text("Daily Downtime Report", 40, 30);

    const headers = [
      [
        "Unit Name",
        "Time Start",
        "Time End",
        "Fault Code",
        "Fault",
        "Fault Type",
        "Downtime (hrs)",
        "Customer Time (hrs)",
        "Company Time (hrs)",
        "Control Type",
      ],
    ];

    const rows = filteredReportData.map((r) => [
      r.unitName,
      r.timeStart,
      r.timeEnd,
      r.faultCode,
      r.fault,
      r.faultType,
      r.downtime,
      r.customerTime,
      r.companyTime,
      r.ctrlType,
    ]);

    autoTable(doc, {
      head: headers,
      body: rows,
      startY: 50,
      theme: "grid",
      styles: { fontSize: 8 },
      headStyles: { fillColor: [30, 30, 30] },
    });

    doc.save("Daily_Downtime_Report.pdf");
  };

  // ================== UI ==================
  return (
    <div>
      {/* Controls */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
        <input
          type="text"
          placeholder="Search..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="px-3 py-2 w-full sm:w-64 rounded-md border text-white border-gray-300 bg-transparent placeholder-gray-400 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        />

        <div className="flex flex-wrap gap-2">
          <select
            value={selectedDay}
            onChange={(e) => setSelectedDay(e.target.value)}
            className="px-3 py-2 rounded-md border text-white border-gray-300 bg-slate-700 text-sm"
          >
            {dayOptions.map((d) => (
              <option key={d.value} value={d.value}>
                {d.label}
              </option>
            ))}
          </select>

          <button
            onClick={exportToCSV}
            className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors w-[60px] sm:w-auto"
          >
            CSV
          </button>
          <button
            onClick={exportToExcel}
            className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors w-[60px] sm:w-auto"
          >
            Excel
          </button>
          <button
            onClick={exportToPDF}
            className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors w-[60px] sm:w-auto"
          >
            PDF
          </button>
        </div>
      </div>

      <DailyReportDowntimeTable
        reportData={filteredReportData}
        loading={loading}
        error={error}
      />
    </div>
  );
};

export default DailyDownPerGroup;
